import {yupResolver} from '@hookform/resolvers/yup';
import React, {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useDispatch} from 'react-redux';
import * as yup from 'yup';

import {useCurrentLanguage} from '../../../api/hooks/useCurrentLanguage.hook';
import {useRegisterPage as useRegisterPage} from '../../../api/hooks/useRegisterPage.hook';
import {LoginData} from '../../../api/models/login-data';
import {RegisterData} from '../../../api/models/register-data';
import {createUser, RootState, setHasRegistered, useAppSelector} from '../../../redux';
import {ButtonTypesEnum} from '../../../utils/_enums/button-types.enum';
import {EnumToLanguageMapper} from '../../../utils/_helper/enum-to-language-mapper';
import {InputValidation} from '../../../utils/_helper/input-validation';
import {isValidGeoIp as isValidCountry} from '../../../utils/_helper/valid-country-helper';
import {Checkbox} from '../../_shared/checkbox/checkbox';
import {Input} from '../../_shared/input-fields/input/input';
import {Select} from '../../_shared/select/select';
import styles from './register-form.module.scss';
import {AuthenticationFormSubmitButton} from './register-form-submit-button/register-form-submit-button';

interface AuthenticationFormData extends FormData {
    email: string;
    username: string;
    password: string;
    confirmPassword: string;
    agbConfirmed: boolean;
    newsletterConfirmed: boolean;
    dropletterConfirmed: boolean;
    emailOrUsername: string;
    country: string;
}

export const RegisterForm: React.FC = () => {
    const data = useRegisterPage();
    const dispatch = useDispatch();

    const language = useCurrentLanguage();

    const {registerStatePending, hasRegistered} = useAppSelector((state: RootState) => ({
        registerStatePending: state.authentification.pending,
        hasRegistered: state.authentification.hasRegistered})
    );
    const countryOptions = EnumToLanguageMapper.countriesMapping().filter(
        ({value}) => isValidCountry(value.toLowerCase())
    );

    useEffect(() => {
        dispatch(setHasRegistered(false));
    }, []);


    const schema = yup.object({
        country: yup
            .string()
            .required(data?.country?.fieldErrorMessages?.required ?? ''),
        email: yup
            .string()
            .required(data?.email?.fieldErrorMessages?.required ?? '')
            .email(data?.email?.fieldErrorMessages?.valid ?? ''),
        username: yup
            .string()
            .max(16, data?.username?.fieldErrorMessages?.max ?? '')
            .required(data?.username?.fieldErrorMessages?.required ?? ''),
        password: InputValidation.password(
            data?.password?.fieldErrorMessages ?? {id: -1, required: ''}
        ),
        confirmPassword: yup
            .string()
            .oneOf([yup.ref('password'), null], data?.confirmPassword?.fieldErrorMessages?.valid ?? ''),
        agbConfirmed: yup
            .boolean()
            .oneOf([true], data?.agbAdvancedAccept?.fieldErrorMessages?.required ?? '')
    });

    const resolver = yupResolver(schema);
    const {
        register,
        handleSubmit,
        control,
        formState: {errors}
    } = useForm<AuthenticationFormData>({
        resolver,
        mode: 'onChange'
    });

    const dataSubmitted = (data: LoginData | RegisterData) => {
        dispatch(
            createUser({
                ...(data as RegisterData),
                language: language.toUpperCase()
            } as RegisterData)
        );
    };

    if (data) {
        return (
                <form
                    noValidate
                    className={styles['authentification-form']}
                    onSubmit={handleSubmit(dataSubmitted)}
                >
                    {[
                        <Input
                            key="email-input"
                            register={register}
                            title={data?.email?.fieldLabel}
                            type="email"
                            name="email"
                            placeholder={data?.email?.fieldHint}
                            errorMessage={(errors as any)?.['email']?.message}
                        />,
                        <Input
                            key="username-input"
                            register={register}
                            title={data?.username?.fieldLabel}
                            type="username"
                            name="username"
                            placeholder={data?.username?.fieldHint}
                            errorMessage={
                                (errors as any)?.['username']?.message
                            }
                        />,
                        <Input
                            key="password-input"
                            register={register}
                            name="password"
                            title={data?.password?.fieldLabel}
                            placeholder={data?.password?.fieldHint}
                            errorMessage={
                                (errors as any)?.['password']?.message
                            }
                            type="password"
                        />,
                        <Input
                            key="confirm-password-input"
                            register={register}
                            name="confirmPassword"
                            title={data?.confirmPassword?.fieldLabel}
                            placeholder={data?.confirmPassword?.fieldHint}
                            errorMessage={
                                (errors as any)?.['confirmPassword']?.message
                            }
                            type="password"
                        />,
                        <Select
                            key="country-input"
                            control={control}
                            label={data?.country?.fieldLabel}
                            name="country"
                            placeholder={data?.country?.fieldHint}
                            errorMessage={(errors as any)?.['country']?.message}
                            options={countryOptions}
                        />,
                        <Checkbox
                            register={register}
                            key="agb-checkbox"
                            name="agbConfirmed"
                            errorMessage={
                                (errors as any)?.['agbConfirmed']?.message
                            }
                        >
                            {data?.agbAdvancedAccept?.fieldLabel}
                        </Checkbox>,
                        <Checkbox
                            register={register}
                            key="newsletter-checkbox"
                            name="newsletterConfirmed"
                        >
                            {data?.newsletterAccept?.fieldLabel}
                        </Checkbox>,
                        <Checkbox
                            register={register}
                            key="dropletter-checkbox"
                            name="dropletterConfirmed"
                        >
                            {data?.dropletterAccept?.fieldLabel}
                        </Checkbox>,
                        <AuthenticationFormSubmitButton
                            pending={registerStatePending || hasRegistered}
                            type={ButtonTypesEnum.SUBMIT}
                            key="register-submit-button"
                        >
                            {data?.registerButton?.buttonText}
                        </AuthenticationFormSubmitButton>
                    ]}
                </form>
        );
    } else {
        return null;
    }
};
